// =============================================================================
// GOUV LOGIN HISTORY — Historique de connexion (vue globale + par user + filtres + CSV)
// Onglet "Connexions" dans Gouvernance → Sécurité
// =============================================================================
import { useState, useEffect, useCallback } from "react";
import {
  History, Download, Filter, RefreshCw, Loader2, LogIn,
  User, ChevronLeft, ChevronRight, Search, X,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const PAGE_SIZE = 50;

const SOURCE_LABELS = {
  password: "Login email",
  google_sso: "Google SSO",
  dal_mobile: "DAL Mobile",
  impersonation: "Impersonation",
};

const ROLE_OPTIONS = [
  { value: "", label: "Tous les rôles" },
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "user", label: "Utilisateur" },
  { value: "external", label: "User Externe" },
  { value: "installateur", label: "Installateur" },
];

function getHeaders() {
  const token = localStorage.getItem("session_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function GouvLoginHistory({ users = [] }) {
  const [view, setView] = useState("global"); // global | per_user
  const [data, setData] = useState({ items: [], total: 0, offset: 0, limit: PAGE_SIZE });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);

  // Filtres
  const [filters, setFilters] = useState({
    user_id: "",
    role: "",
    date_from: "",
    date_to: "",
    success: "",
    source: "",
  });

  // Selected user pour vue per_user
  const [selectedUserId, setSelectedUserId] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));
      const effectiveUser = view === "per_user" ? selectedUserId : filters.user_id;
      if (effectiveUser) params.set("user_id", effectiveUser);
      if (view === "global") {
        if (filters.role) params.set("role", filters.role);
        if (filters.date_from) params.set("date_from", filters.date_from);
        if (filters.date_to) params.set("date_to", filters.date_to);
        if (filters.success) params.set("success", filters.success);
        if (filters.source) params.set("source", filters.source);
      }

      const res = await fetch(`${API}/api/hub/login-history?${params}`, {
        headers: getHeaders(), credentials: "include",
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur de chargement");
        setData({ items: [], total: 0, offset: 0, limit: PAGE_SIZE });
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, [view, page, filters, selectedUserId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reset page sur changement de filtres / vue
  useEffect(() => { setPage(0); }, [view, filters, selectedUserId]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      const effectiveUser = view === "per_user" ? selectedUserId : filters.user_id;
      if (effectiveUser) params.set("user_id", effectiveUser);
      if (view === "global") {
        if (filters.role) params.set("role", filters.role);
        if (filters.date_from) params.set("date_from", filters.date_from);
        if (filters.date_to) params.set("date_to", filters.date_to);
        if (filters.success) params.set("success", filters.success);
        if (filters.source) params.set("source", filters.source);
      }
      const res = await fetch(`${API}/api/hub/login-history.csv?${params}`, {
        headers: getHeaders(), credentials: "include",
      });
      if (!res.ok) {
        toast.error("Erreur d'export");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Tente de récupérer le filename depuis Content-Disposition
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "login-history.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setFilters({ user_id: "", role: "", date_from: "", date_to: "", success: "", source: "" });
    setSelectedUserId("");
    setUserSearch("");
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const filteredUsers = users.filter((u) => {
    if (!userSearch) return true;
    const q = userSearch.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  const renderRow = (evt, idx) => {
    const ctx = evt.context || {};
    const sourceLabel = SOURCE_LABELS[evt.source] || evt.source || "—";
    return (
      <tr key={idx} className="hover:bg-zinc-800/30">
        <td className="px-3 py-2 text-zinc-200 font-mono text-xs whitespace-nowrap">{formatTs(evt.ts)}</td>
        <td className="px-3 py-2 text-xs">
          <div className="text-zinc-200">{evt.user_name || evt.email || "—"}</div>
          <div className="text-zinc-500 text-[10px] font-mono">{evt.email}</div>
        </td>
        <td className="px-3 py-2 text-xs">
          {evt.user_role ? (
            <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">{evt.user_role}</Badge>
          ) : "—"}
        </td>
        <td className="px-3 py-2 text-zinc-400 font-mono text-xs whitespace-nowrap">{evt.ip || "—"}</td>
        <td className="px-3 py-2 text-xs">
          <span className="text-zinc-300">{sourceLabel}</span>
          {ctx.dossier_reference && (
            <span className="block text-zinc-500 mt-0.5 text-[10px]">
              {ctx.dossier_reference}{ctx.auth_method ? ` · ${ctx.auth_method}` : ""}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-xs">
          {evt.tenant_id ? <span className="text-zinc-400">{evt.tenant_id}</span> : <span className="text-zinc-600">—</span>}
        </td>
        <td className="px-3 py-2">
          {evt.success ? (
            <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">OK</Badge>
          ) : (
            <Badge className="bg-red-500/20 text-red-400 text-xs" title={evt.failure_reason || ""}>
              Échec{evt.failure_reason ? ` · ${evt.failure_reason}` : ""}
            </Badge>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header avec toggle vue + actions */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <History className="w-5 h-5 text-emerald-400" />
            Historique des connexions
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {data.total} événement{data.total > 1 ? "s" : ""} dans la sélection courante
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {/* Toggle global / per_user */}
          <div className="flex rounded-lg border border-zinc-700 overflow-hidden">
            <button
              onClick={() => setView("global")}
              className={`px-3 py-1.5 text-xs font-medium ${view === "global" ? "bg-emerald-500/20 text-emerald-400" : "text-zinc-400 hover:text-zinc-200"}`}
              data-testid="login-history-view-global"
            >
              Vue globale
            </button>
            <button
              onClick={() => setView("per_user")}
              className={`px-3 py-1.5 text-xs font-medium ${view === "per_user" ? "bg-emerald-500/20 text-emerald-400" : "text-zinc-400 hover:text-zinc-200"}`}
              data-testid="login-history-view-user"
            >
              Vue par user
            </button>
          </div>
          <Button variant="ghost" size="sm" onClick={loadData} disabled={loading} className="text-zinc-400 hover:text-white">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button onClick={exportCSV} disabled={exporting} size="sm" className="bg-emerald-600 hover:bg-emerald-500">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
            CSV
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
        {view === "per_user" ? (
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1">
              <Label className="text-xs text-zinc-400 mb-1 block">Utilisateur</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2 top-2.5" />
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Rechercher par nom ou email..."
                  className="bg-zinc-800 border-zinc-700 pl-7 h-9"
                />
                {userSearch && (
                  <button onClick={() => setUserSearch("")} className="absolute right-2 top-2.5 text-zinc-500 hover:text-zinc-200">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {userSearch && (
                <div className="mt-1 max-h-40 overflow-y-auto border border-zinc-700 rounded bg-zinc-800/50">
                  {filteredUsers.slice(0, 10).map((u) => (
                    <button
                      key={u.user_id || u._id || u.email}
                      onClick={() => {
                        setSelectedUserId(u.user_id || u.email);
                        setUserSearch(u.name || u.email);
                      }}
                      className="block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-700 text-zinc-200"
                    >
                      <div>{u.name || u.email}</div>
                      <div className="text-[10px] text-zinc-500">{u.email} · {u.role}</div>
                    </button>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div className="px-3 py-2 text-xs text-zinc-500">Aucun utilisateur</div>
                  )}
                </div>
              )}
            </div>
            {selectedUserId && (
              <Button variant="ghost" size="sm" onClick={() => { setSelectedUserId(""); setUserSearch(""); }} className="text-zinc-400">
                <X className="w-3.5 h-3.5 mr-1" /> Réinitialiser
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-zinc-400 mb-1 block">Rôle</Label>
              <Select value={filters.role || "all"} onValueChange={(v) => setFilters({ ...filters, role: v === "all" ? "" : v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {ROLE_OPTIONS.map((r) => <SelectItem key={r.value || "all"} value={r.value || "all"}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1 block">Source</Label>
              <Select value={filters.source || "all"} onValueChange={(v) => setFilters({ ...filters, source: v === "all" ? "" : v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">Toutes les sources</SelectItem>
                  {Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1 block">Statut</Label>
              <Select value={filters.success || "all"} onValueChange={(v) => setFilters({ ...filters, success: v === "all" ? "" : v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="true">Réussies</SelectItem>
                  <SelectItem value="false">Échouées</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1 block">Du</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="bg-zinc-800 border-zinc-700 h-9 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-1 block">Au</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="bg-zinc-800 border-zinc-700 h-9 text-xs"
              />
            </div>
            <div className="flex items-end">
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-zinc-400 hover:text-white h-9 w-full">
                <X className="w-3.5 h-3.5 mr-1" /> Réinitialiser
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Tableau */}
      <div className="rounded-lg border border-zinc-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
          </div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-16 text-zinc-500">
            <LogIn className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Aucune connexion dans la sélection</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] text-sm">
              <thead className="bg-zinc-800/50 text-zinc-400 uppercase tracking-wide text-xs">
                <tr>
                  <th className="px-3 py-2 text-left">Date / Heure</th>
                  <th className="px-3 py-2 text-left">Utilisateur</th>
                  <th className="px-3 py-2 text-left">Rôle</th>
                  <th className="px-3 py-2 text-left">IP</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Tenant</th>
                  <th className="px-3 py-2 text-left">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {data.items.map(renderRow)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <div>
            Page {page + 1} / {totalPages} — {data.total} événement{data.total > 1 ? "s" : ""}
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="text-zinc-400">
              <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Préc.
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className="text-zinc-400">
              Suiv. <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
